<script>
  import { emitter } from '@/main.js';
  import { debounce, whenFastSimonReady, handleViewportSize } from '@/lib/vue-helpers.js';

  export default {
    name: 'FsSearchField',
    mounted() {
      this.handleViewportSize = handleViewportSize(this);
      this.searchMessage = document.getElementById('fs-search-field').getAttribute('data-search-message');
      this.debouncedOnInput = debounce(this.handleInput, 150, false);

      emitter.on('fs-update', e => {
        this.query = e.query;
      });
    },
    beforeUnmount() {
      if (this.handleViewportSize) this.handleViewportSize();
    },
    data() {
      return {
        searchMessage: '',
        query: '',
        initialScrollPosition: 0,
        isNavigating: false
      };
    },
    methods: {
      handleInput(e) {
        if (this.isNavigating) return;
        this.query = e.target.value;
      },
      // Added focus/blur handlers to manage header visibility while keyboard is open on iOS
      onFocus() {
        if (this.isIOS()) {
          // Store the current scroll position before focus
          this.initialScrollPosition = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;

          // Only hide header and add keyboard class if we're not at the top
          // When at the top, we don't need to hide the header to make room for keyboard
          if (this.initialScrollPosition > 10) {
            // Add class to help prevent scroll adjustments
            document.body.classList.add('keyboard-open');
            this.hideHeader();
          }

          // If we're at or very close to the top, prevent iOS auto-scroll
          if (this.initialScrollPosition <= 5) {
            // Use a short delay to override iOS's automatic scrolling
            setTimeout(() => {
              this.preventAutoScroll();
            }, 10);

            // Additional prevention with multiple timeouts to catch delayed scrolling
            setTimeout(() => {
              this.preventAutoScroll();
            }, 50);

            setTimeout(() => {
              this.preventAutoScroll();
            }, 100);
          }
        }
      },
      onBlur() {
        if (this.isIOS()) {
          // Remove keyboard class and restore header if they were modified
          document.body.classList.remove('keyboard-open');

          // give the keyboard time to close before restoring header
          setTimeout(() => {
            this.showHeader();
          }, 300);
        }
      },
      preventAutoScroll() {
        // Force scroll back to original position if iOS moved it
        const currentScroll = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;

        // Only intervene if we were originally at the top and iOS has scrolled us down
        if (this.initialScrollPosition <= 5 && currentScroll > this.initialScrollPosition + 5) {
          try {
            window.scrollTo({ top: this.initialScrollPosition, behavior: 'instant' });
          } catch (e) {
            window.scrollTo(0, this.initialScrollPosition);
          }
          document.documentElement.scrollTop = this.initialScrollPosition;
          document.body.scrollTop = this.initialScrollPosition;
        }
      },
      hideHeader() {
        const header = document.querySelector('.theme__header');
        if (header) {
          header.classList.add('header-hidden');
        }
        document.body.classList.add('header-hidden-fallback');
      },
      showHeader() {
        const header = document.querySelector('.theme__header');
        if (header) {
          header.classList.remove('header-hidden');
        }
        document.body.classList.remove('header-hidden-fallback');
      },
      isIOS() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      },
      submitForm() {
        const phrase = this.query.trim();
        if (!phrase) return;
        this.debouncedOnInput.cancel();
        this.isNavigating = true;
        this.resolveTurbolink(phrase, true);
      },
      resolveTurbolink(term, updateSrpInPlace) {
        const phrase = term.trim();
        if (!phrase) return;
        let done = false;
        const applySrpSearchIfNeeded = () => {
          if (done) return;
          if (!updateSrpInPlace) return;
          done = true;
          emitter.emit('fs-search-update', phrase);
          this.setUrlParam('q', phrase);
          this.scrollToTop();
        };
        whenFastSimonReady(
          () => {
            // Turbolink lives in payload.turbolink, not action; callback may fire twice before final pass.
            const srpTimer = setTimeout(applySrpSearchIfNeeded, 4000);
            window.FastSimonSDK.fullTextSearch({
              term: phrase,
              callback: response => {
                if (done) return;
                const turbolinkUrl = response?.payload?.turbolink?.u;
                if (turbolinkUrl) {
                  clearTimeout(srpTimer);
                  done = true;
                  this.setTurbolinkCollectionFlag(turbolinkUrl);
                  window.location.href = turbolinkUrl;
                  return;
                }
                if (response?.action === 'facets and products') {
                  clearTimeout(srpTimer);
                  applySrpSearchIfNeeded();
                }
              }
            });
          },
          { onTimeout: applySrpSearchIfNeeded }
        );
      },
      setTurbolinkCollectionFlag(url) {
        if (url.includes('/collections/')) {
          localStorage.setItem('fromTurboLink', 'true');
        }
      },
      scrollToTop() {
        // Ensure we land fully at the top (scrollY ~ 0) even with iOS keyboard/layout quirks
        // Strategy: keep forcing scrollTo(0) until scrollY <= 1, then proactively
        // trigger CatNavigationManager updates by dispatching a scroll event and
        // removing any header-hidden classes.

        const retryLimit = 8;
        let tries = 0;

        const forceTop = () => {
          try {
            window.scrollTo({ top: 0, behavior: 'instant' });
          } catch (e) {
            window.scrollTo(0, 0);
          }
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        };

        const finalize = () => {
          // Reveal header / announcement
          const header = document.querySelector('.theme__header');
          header?.classList.remove('header-hidden');
          document.body.classList.remove('header-hidden-fallback', 'sticky-active');
          document.body.classList.add('at-top');

          // Let CatNavigationManager recompute state
          window.dispatchEvent(new Event('scroll'));
        };

        const verify = () => {
          const current = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
          if (current > 1 && tries < retryLimit) {
            tries += 1;
            forceTop();
            setTimeout(verify, 16);
          } else {
            finalize();
          }
        };

        requestAnimationFrame(() => {
          forceTop();
          // Give layout one frame before verifying
          setTimeout(verify, 16);
        });
      },
      setUrlParam(paramName, paramValue) {
        const url = new URL(window.location.href);
        url.searchParams.set(paramName, paramValue);
        url.searchParams.set('filters', '');
        url.searchParams.delete('current');
        window.history.replaceState(null, null, url);
      },
      onTouchStart() {
        // iOS Safari fix: Ensure the input can receive focus on touch
        // Don't prevent default to allow native text selection behaviors
        // FastClick will handle this input field appropriately
        this.$refs.searchInput.focus();
      },
      onInputClick() {
        // Additional click handler to ensure focus on iOS
        this.$refs.searchInput.focus();
      },
      onDoubleClick() {
        // Explicitly handle double-click for text selection on iOS
        this.$refs.searchInput.select();
      }
    }
  };
</script>

<template>
  <h4 v-html="searchMessage" class="gt-h4"></h4>
  <input type="text" id="search-field-value" :value="query" @keyup.enter="submitForm" @click="onInputClick" @dblclick="onDoubleClick" @input="debouncedOnInput" @touchstart="onTouchStart" @focus="onFocus" @blur="onBlur" class="gt-input fs-search-input" ref="searchInput" />
</template>

<style lang="scss">
  .fs-search-input {
    // iOS Safari specific fixes
    .is-mobile & {
      min-height: 44px; // iOS minimum touch target size
      padding: 8px 12px;
      border-radius: 0;
      -webkit-appearance: none;
      appearance: none;
      touch-action: manipulation;
      -webkit-user-select: text;
      user-select: text;
      -webkit-touch-callout: default;
      -webkit-tap-highlight-color: transparent;
      cursor: text;

      // Additional properties to prevent auto-scroll
      transform: translateZ(0); // Force hardware acceleration
      -webkit-overflow-scrolling: touch;
      position: relative;
      z-index: 1;
    }
  }
</style>
